import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function PricingComparison() {
  const { t } = useTranslation();

  const plan1Features = t('plan1_features', { returnObjects: true }) as string[];
  const plan2Features = t('plan2_features', { returnObjects: true }) as string[];

  return (
    <section className="py-24 bg-[#FCFAFD] px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-3">
            Transparent Pricing
          </p>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('pricing_title')}
          </h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            {t('pricing_subtitle')}
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Plan 1 */}
          <div className="relative rounded-2xl border-2 border-[#E9D9F2] bg-white p-8 flex flex-col">
            <div className="mb-6">
              <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-3">
                {t('plan1_name')}
              </p>
              <div className="flex items-end gap-1 mb-1">
                <span className="text-5xl font-bold text-gray-900">{t('plan1_price')}</span>
              </div>
              <p className="text-base font-semibold text-[#4F2D63] mb-1">{t('plan1_plus')}</p>
              <p className="text-sm text-gray-500 italic">{t('plan1_when')}</p>
            </div>

            <div className="h-px bg-gray-100 mb-6"></div>

            <ul className="space-y-3 flex-1 mb-8">
              {(Array.isArray(plan1Features) ? plan1Features : []).map((feature: string) => (
                <li key={feature} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-[#0F766E] text-sm font-bold"></i>
                  </div>
                  <span className="text-sm text-gray-700 leading-relaxed">{feature}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/contact"
              className="w-full py-4 rounded-xl bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold text-sm text-center transition-colors cursor-pointer whitespace-nowrap block"
            >
              {t('plan1_cta')}
            </Link>
          </div>

          {/* Plan 2 */}
          <div className="relative rounded-2xl border-2 border-[#4F2D63] bg-[#F8F3FB] p-8 flex flex-col">
            {/* Popular badge */}
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
              <span className="px-4 py-1.5 rounded-full bg-amber-500 text-white text-xs font-bold whitespace-nowrap">
                ★ {t('pricing_popular')}
              </span>
            </div>

            <div className="mb-6">
              <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-3">
                {t('plan2_name')}
              </p>
              <div className="flex items-end gap-1 mb-1">
                <span className="text-5xl font-bold text-gray-900">{t('plan2_price')}</span>
              </div>
              <p className="text-base font-semibold text-[#4F2D63] mb-1">{t('plan2_plus')}</p>
              <p className="text-sm text-gray-500 italic">{t('plan2_when')}</p>
            </div>

            <div className="h-px bg-[#E9D9F2] mb-6"></div>

            <ul className="space-y-3 flex-1 mb-8">
              {(Array.isArray(plan2Features) ? plan2Features : []).map((feature: string) => (
                <li key={feature} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-[#0F766E] text-sm font-bold"></i>
                  </div>
                  <span className="text-sm text-gray-700 leading-relaxed">{feature}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/contact"
              className="w-full py-4 rounded-xl bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold text-sm text-center transition-colors cursor-pointer whitespace-nowrap block"
            >
              {t('plan2_cta')}
            </Link>
          </div>
        </div>

        {/* Helper text */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            {t('pricing_help')}{' '}
            <Link to="/contact" className="text-[#4F2D63] font-semibold hover:text-[#3E234F] cursor-pointer">
              {t('pricing_compare')} →
            </Link>
          </p>
          <p className="text-xs text-gray-400 mt-4 max-w-2xl mx-auto italic">
            {t('pricing_disclaimer')}
          </p>
        </div>
      </div>
    </section>
  );
}
